import { jsPDF } from 'jspdf'
import * as XLSX from 'xlsx'

function triggerBlobDownload(filename, blob) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename
  document.body.appendChild(a); a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function toCSVString(rows, columns) {
  const esc = v => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s }
  return [columns.map(c => esc(c.label)).join(','), ...rows.map(r => columns.map(c => esc(r[c.key])).join(','))].join('\n')
}

export function downloadCSV(filename, rows, columns) {
  triggerBlobDownload(filename, new Blob([toCSVString(rows, columns)], { type: 'text/csv;charset=utf-8;' }))
}

export function downloadXLSX(filename, sheetName, rows, columns) {
  const data = rows.map(r => { const o = {}; columns.forEach(c => { o[c.label] = r[c.key] }); return o })
  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31))
  XLSX.writeFile(wb, filename)
}

export function generateInvoicePDF(invoice, pharmacy) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 48
  let y = 56

  // Logo slot
  const logoImg = document.querySelector('.brand-logo')
  let logoDrawn = false
  if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
    try {
      const canvas = document.createElement('canvas')
      canvas.width = logoImg.naturalWidth; canvas.height = logoImg.naturalHeight
      canvas.getContext('2d').drawImage(logoImg, 0, 0)
      const logoH = 32, logoW = (logoImg.naturalWidth / logoImg.naturalHeight) * logoH
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', margin, y - 24, logoW, logoH)
      logoDrawn = true
    } catch (e) { /* cross-origin fallback */ }
  }
  if (!logoDrawn) { doc.setFont('helvetica','bold'); doc.setFontSize(16); doc.text('Hetero Labs Ltd.', margin, y) }

  doc.setFont('helvetica','bold'); doc.setFontSize(20)
  doc.text('TAX INVOICE', pageWidth - margin, y, { align: 'right' })
  y += 34

  doc.setDrawColor(15, 61, 62); doc.setLineWidth(1.2)
  doc.line(margin, y, pageWidth - margin, y); y += 24

  doc.setFont('helvetica','normal'); doc.setFontSize(10); doc.setTextColor(90,90,90)
  doc.text('Invoice No.', margin, y); doc.text('Invoice Date', margin+160, y); doc.text('Order No.', margin+320, y)
  y += 14
  doc.setTextColor(20,20,20); doc.setFont('helvetica','bold')
  doc.text(invoice.invoiceNo || '—', margin, y); doc.text(invoice.invoiceDate || '—', margin+160, y); doc.text(invoice.orderNo, margin+320, y)
  y += 30

  doc.setFont('helvetica','bold'); doc.setFontSize(11); doc.setTextColor(15,61,62)
  doc.text('Bill To', margin, y); y += 16
  doc.setFont('helvetica','normal'); doc.setFontSize(10); doc.setTextColor(20,20,20)
  doc.text(invoice.pharmacyName, margin, y); y += 13
  if (pharmacy) { doc.text(pharmacy.address + ', ' + pharmacy.city, margin, y); y += 13; doc.text('Drug License: ' + pharmacy.drugLicenseNo, margin, y); y += 13 }
  y += 20

  const colX = [margin, margin+260, margin+360, margin+460]
  const rowH = 24
  doc.setFillColor(15,61,62); doc.rect(margin, y, pageWidth-margin*2, rowH, 'F')
  doc.setTextColor(255,255,255); doc.setFont('helvetica','bold'); doc.setFontSize(10)
  doc.text('Description', colX[0]+8, y+16); doc.text('Base (USD)', colX[1]+8, y+16)
  doc.text('VAT 15%', colX[2]+8, y+16); doc.text('Total (USD)', colX[3]+8, y+16)
  y += rowH
  doc.setTextColor(20,20,20); doc.setFont('helvetica','normal')
  doc.rect(margin, y, pageWidth-margin*2, rowH)
  doc.text(invoice.productName, colX[0]+8, y+16); doc.text('$'+invoice.baseValue.toLocaleString(), colX[1]+8, y+16)
  doc.text('$'+invoice.taxAmount.toLocaleString(), colX[2]+8, y+16); doc.text('$'+invoice.invoiceValue.toLocaleString(), colX[3]+8, y+16)
  y += rowH + 20

  doc.setFont('helvetica','bold'); doc.setFontSize(12)
  doc.text('Total Due: $' + invoice.invoiceValue.toLocaleString(), pageWidth-margin, y, { align: 'right' })
  y += 30
  doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(120,120,120)
  doc.text('Billing Stage: ' + invoice.billingStage, margin, y); y += 40
  doc.setFontSize(8)
  doc.text('Prototype document — Pharmacy First / Hetero Labs Ltd.', margin, y)
  doc.save((invoice.invoiceNo || invoice.orderNo) + '.pdf')
}

// Multi-item version for the new order model (point 5: orders can carry
// several line items, so the invoice needs a real multi-row table rather
// than the single-productName row the old generateInvoicePDF draws).
export function generateInvoicePDFv2(order, pharmacy) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 48
  let y = 56

  const logoImg = document.querySelector('.brand-logo')
  let logoDrawn = false
  if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
    try {
      const canvas = document.createElement('canvas')
      canvas.width = logoImg.naturalWidth; canvas.height = logoImg.naturalHeight
      canvas.getContext('2d').drawImage(logoImg, 0, 0)
      const logoH = 32, logoW = (logoImg.naturalWidth / logoImg.naturalHeight) * logoH
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', margin, y - 24, logoW, logoH)
      logoDrawn = true
    } catch (e) { /* cross-origin fallback */ }
  }
  if (!logoDrawn) { doc.setFont('helvetica','bold'); doc.setFontSize(16); doc.text('Hetero Labs Ltd.', margin, y) }

  doc.setFont('helvetica','bold'); doc.setFontSize(20)
  doc.text('TAX INVOICE', pageWidth - margin, y, { align: 'right' })
  y += 34
  doc.setDrawColor(15, 61, 62); doc.setLineWidth(1.2)
  doc.line(margin, y, pageWidth - margin, y); y += 24

  const inv = order.invoice
  doc.setFont('helvetica','normal'); doc.setFontSize(10); doc.setTextColor(90,90,90)
  doc.text('Invoice No.', margin, y); doc.text('Invoice Date', margin+160, y); doc.text('Order No.', margin+320, y)
  y += 14
  doc.setTextColor(20,20,20); doc.setFont('helvetica','bold')
  doc.text(inv.invoiceNo || '—', margin, y); doc.text(inv.invoiceDate || '—', margin+160, y); doc.text(order.orderNo, margin+320, y)
  y += 30

  doc.setFont('helvetica','bold'); doc.setFontSize(11); doc.setTextColor(15,61,62)
  doc.text('Bill To', margin, y); y += 16
  doc.setFont('helvetica','normal'); doc.setFontSize(10); doc.setTextColor(20,20,20)
  doc.text(pharmacy?.pharmacyName || order.pharmacyName || '', margin, y); y += 13
  if (pharmacy) { doc.text((pharmacy.address||'') + (pharmacy.city ? ', '+pharmacy.city : ''), margin, y); y += 13; doc.text('Drug License: ' + (pharmacy.drugLicenseNo||''), margin, y); y += 13 }
  y += 16

  const colX = [margin, margin+250, margin+310, margin+380, margin+450]
  const rowH = 22
  doc.setFillColor(15,61,62); doc.rect(margin, y, pageWidth-margin*2, rowH, 'F')
  doc.setTextColor(255,255,255); doc.setFont('helvetica','bold'); doc.setFontSize(9)
  doc.text('Product', colX[0]+8, y+15); doc.text('Ordered', colX[1]+8, y+15)
  doc.text('Free', colX[2]+8, y+15); doc.text('Delivered', colX[3]+8, y+15); doc.text('Line Total', colX[4]+8, y+15)
  y += rowH
  doc.setTextColor(20,20,20); doc.setFont('helvetica','normal'); doc.setFontSize(9)
  order.lineItems.forEach((li, i) => {
    if (i % 2 === 0) { doc.setFillColor(247,246,242); doc.rect(margin, y, pageWidth-margin*2, rowH, 'F') }
    doc.text(String(li.productName).slice(0, 30) + (li.schemeApplied ? ' (10+1)' : ''), colX[0]+8, y+15)
    doc.text(String(li.quantity), colX[1]+8, y+15)
    doc.text(String(li.freeQuantity || 0), colX[2]+8, y+15)
    doc.text(String(li.totalQuantity || li.quantity), colX[3]+8, y+15)
    doc.text('$'+li.lineValue.toLocaleString(), colX[4]+8, y+15)
    y += rowH
  })
  y += 14

  doc.setFont('helvetica','normal'); doc.setFontSize(10); doc.setTextColor(90,90,90)
  doc.text('Base Value', margin+340, y); doc.text('$'+inv.baseValue.toLocaleString(), pageWidth-margin, y, { align: 'right' }); y += 16
  doc.text('VAT 15%', margin+340, y); doc.text('$'+inv.taxAmount.toLocaleString(), pageWidth-margin, y, { align: 'right' }); y += 20
  doc.setFont('helvetica','bold'); doc.setFontSize(12); doc.setTextColor(20,20,20)
  doc.text('Total Due', margin+340, y); doc.text('$'+inv.invoiceValue.toLocaleString(), pageWidth-margin, y, { align: 'right' })
  y += 30

  doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(120,120,120)
  doc.text('Billing Stage: ' + inv.billingStage, margin, y); y += 40
  doc.setFontSize(8)
  doc.text('Prototype document — Pharmacy First / Hetero Labs Ltd.', margin, y)
  doc.save((inv.invoiceNo || order.orderNo) + '.pdf')
}

export function generateReportPDF(title, rows, columns) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 36
  let y = 44
  doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(201,154,46)
  doc.text('HETERO LABS LTD. — PHARMACY FIRST', margin, y); y += 18
  doc.setFontSize(16); doc.setTextColor(15,61,62); doc.text(title, margin, y); y += 20
  const usableWidth = pageWidth - margin*2
  const colW = usableWidth / columns.length
  const rowH = 20
  const drawHeader = () => {
    doc.setFillColor(15,61,62); doc.rect(margin, y, usableWidth, rowH, 'F')
    doc.setTextColor(255,255,255); doc.setFont('helvetica','bold'); doc.setFontSize(8.5)
    columns.forEach((c,i) => doc.text(String(c.label), margin+i*colW+6, y+14)); y += rowH
    // BUG FIX: this left white/bold text active for whatever rendered next.
    // On single-page reports the first row's own explicit reset (below,
    // before the loop) papered over it — but on a page break, drawHeader()
    // is called mid-loop with no reset after it, so every row following a
    // page break was rendered in white text on a light background:
    // technically present, completely invisible. Restoring here fixes it
    // regardless of which page drawHeader() is called from.
    doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(20,20,20)
  }
  drawHeader()
  doc.setFont('helvetica','normal'); doc.setTextColor(20,20,20)
  rows.forEach((r,idx) => {
    if (y > pageHeight-40) { doc.addPage(); y = 44; drawHeader() }
    if (idx%2===0) { doc.setFillColor(247,246,242); doc.rect(margin,y,usableWidth,rowH,'F') }
    columns.forEach((c,i) => { const v=r[c.key]; doc.text(v===null||v===undefined?'—':String(v).slice(0,40), margin+i*colW+6, y+14) })
    y += rowH
  })
  doc.save(title.replace(/\s+/g,'_')+'.pdf')
}
